import has from 'lodash/has';
import capitalize from 'lodash/capitalize';

import { TAX_NORMAL_TYPES, TAX_TYPES } from '@guestyci/shared-enums';

/* eslint-disable no-unused-expressions */
import currencySymbolMap from 'constants/currencies';
import moment from 'moment';

import { DATE_FORMAT } from 'constants/date';

/* eslint-disable import/prefer-default-export */

/**
 * @description retrieves currency symbol from provided dictionary.
 * @param {string} currencyCode - key that corresponds particular currency symbol.
 * @param {object} currencySymbolMap - dictionary contains key/value currency symbols.
 * @returns {string} string that contains currency symbol.
 */
export function getSymbolFromCurrency(currencyCode, currencySymbolMapParam = {}) {
  if (typeof currencyCode !== 'string') return undefined;
  const code = currencyCode.toUpperCase();
  if (!Object.prototype.hasOwnProperty.call(currencySymbolMapParam, code)) return undefined;
  return currencySymbolMap[code];
}

/**
 * @description formats price value with appropriate currency symbol.
 * @param {any} price - price that has to be formatted with currency.
 * @param {string} currency - key that corresponds particular currency symbol.
 * @returns {string} string that contains price formatted with currency symbol.
 * @example €1,941.92
 */
export function formatPriceWithCurrency(price, symbol = '') {
  return price && `${symbol}${price.toLocaleString('en')}`;
}

/**
 * @description formats price value with appropriate currency symbol.
 * @param {number} price - price that has to be formatted with currency.
 * @param {string} currency - key that corresponds particular currency symbol.
 * @returns {string} string that contains price formatted with currency symbol.
 */
export function getPriceStr(price, currency) {
  return formatPriceWithCurrency(price, getSymbolFromCurrency(currency, currencySymbolMap));
}

export function formatTaxFeeTitle({ title, normalType, translatedLabel }) {
  const isTax = has(TAX_NORMAL_TYPES, normalType);
  const isCustomTaxName = isTax && !Object.values(TAX_TYPES).includes(title);
  if (isCustomTaxName) {
    return title;
  }
  return translatedLabel || capitalize(title?.replaceAll('_', ' '));
}

// TODO: manage all query params in one place
export function generateSearchParams(
  {
    dates = {},
    location = {},
    guests,
    minPrice,
    maxPrice,
    currency,
    propertyType,
    includeAmenities,
    numberOfBedrooms,
    numberOfBathrooms,
  },
  searchParams = new URLSearchParams(window.location.search)
) {
  const { startDate = undefined, endDate = undefined } = dates;
  const { city = undefined, country = undefined } = location;
  const searchParamKeys = [
    'checkIn',
    'checkOut',
    'city',
    'country',
    'minOccupancy',
    'currency',
    'minPrice',
    'maxPrice',
    'currency',
    'propertyType',
    'includeAmenities',
    'numberOfBedrooms',
    'numberOfBathrooms',
    'lang',
  ];

  searchParamKeys.forEach((key) => searchParams.delete(key));

  if (city && country) {
    searchParams.append('city', city);
    searchParams.append('country', country);
  }

  if (guests && !Number.isNaN(Number(guests))) {
    searchParams.append('minOccupancy', guests);
  }

  if (startDate) {
    searchParams.append('checkIn', moment(startDate).format(DATE_FORMAT));
  }
  if (endDate) {
    searchParams.append('checkOut', moment(endDate).format(DATE_FORMAT));
  }

  if (minPrice) {
    searchParams.append('minPrice', minPrice);
  }

  if (maxPrice) {
    searchParams.append('maxPrice', maxPrice);
  }

  if (minPrice || (maxPrice && currency)) {
    searchParams.append('currency', currency);
  }

  if (propertyType) {
    searchParams.append('propertyType', propertyType);
  }

  if (includeAmenities) {
    searchParams.append('includeAmenities', includeAmenities);
  }

  if (numberOfBedrooms) {
    searchParams.append('numberOfBedrooms', numberOfBedrooms);
  }

  if (numberOfBathrooms) {
    searchParams.append('numberOfBathrooms', numberOfBathrooms);
  }

  return searchParams.toString();
}

function getGuestsItem(guests, maxGuests) {
  const value = String(guests);
  if (value === String(maxGuests)) {
    return { label: guests, value };
  }
  return { label: value, value };
}

export function getGuestItems(guestCount) {
  const arr = new Array(guestCount);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < guestCount; i++) {
    arr[i] = getGuestsItem(i + 1, guestCount);
  }
  return arr;
}

export function transformListingsMarkersResponse({ response = [] }) {
  return response.reduce((accumulator, { position, property }) => {
    if (!position?.lat || !position?.lng) {
      return accumulator;
    }

    const foundMarkersIndex = accumulator.findIndex(
      ({ position: { lng, lat } }) => position.lat === lat && position.lng === lng
    );

    if (foundMarkersIndex === -1) {
      accumulator.push({
        position: { lng: position.lng, lat: position.lat },
        properties: [property],
      });

      return accumulator;
    }

    accumulator[foundMarkersIndex].properties.push(property);

    return accumulator;
  }, []);
}

export function prettifyEnumText(text) {
  return (text.charAt(0) + text.slice(1).toLowerCase()).replace('_', ' ');
}

export function preparePhone(text) {
  return text && text.replace('+', '').replaceAll(' ', '');
}

export function isCloudinaryImage(url) {
  return url?.includes('cloudinary.com/') || url?.includes('assets.guesty.com/');
}

export function joinUrl(baseUrl, relativeUrl) {
  let joinedUrl = `${baseUrl.replace(/\/$/, '')}/${relativeUrl.replace(/^\//, '')}`;
  // Replace any occurrences of "//" with "/"
  joinedUrl = joinedUrl.replace(/\/\//g, '/');
  return joinedUrl;
}
